import _ from "lodash"
import React, { useMemo, useContext, createContext } from "react"
import { Link } from "src/navigation"
import { Helmet } from 'react-helmet-async'
import { useTranslation, Trans } from "react-i18next"

import { useCosts } from "src/costs/CostsProvider"
import { IconArrowUpDown, IconChevronDown, IconChevronUp } from "src/components/icons"
import { useBackend } from "src/BackendProvider"
import { Invention } from "src/inventions/InventionsProvider"
import { inventionUrl } from "src/inventions/utils"
import { familyUrl, memberUrl } from "../utils"
import { createExcelFile } from "src/backend"
//import { downloadReport } from "../../backend"
import { claim_scope, family_member, patent_family } from "src/data"
import { calculatePriorityDate } from "../utils"
import { useMessages } from "src/Messages"
import { useAuth, useRoles } from "src/user/Auth"
import { ImageEntry, PlainImage } from "src/components/Image"
import { useComments } from "src/comments/CommentsProvider"
import { useValuations } from "src/valuations/ValuationsProvider"
import { calcScore } from "src/valuations/Valuations"
import TagList from "src/components/TagList"
import { Family, Member } from "../patents"
import { AgentLink, Agent, nameOf } from "src/agents/utils"
import { Commodity, commodityReferenceUrl } from 'src/products/products'
import { useFilteredPatents } from "src/filter/FilteredPatents"
import { useSingleTeamMangement } from "src/Management"
import { useFxContext } from "src/forecast/FxProvider"
import { accumulateCosts } from "src/costs/utils"
import { usePatents } from "../PatentsProvider"
import { useProducts } from "src/products/ProductsProvider"
import { byFamilyMember, Row, RowsProvider, useGroupableFields, useRowsProvider } from "./Rows"
import { QueriesProvider, QueryDeleteModal, QueryEditorModal, QueryMenu, QueryOpenModal, useQueriesProvider } from "./Queries"
import { useAgents } from "src/agents/AgentsProvider"
import { useTags } from "src/tags/TagsProvider"
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react"
import { useClaims } from "src/claims/ClaimsProvider"
import LoadingButton from "src/components/button/LoadingButton"
import { ClaimScope } from "src/claims/ClaimScope"
import { IncomeLookup, IncomeProvider, useIncome } from "src/income/IncomeProvider"
import { does_patent_cover_income } from "src/income"
import ToggleButton from "src/components/ToggleButton"
import { DwSettingsProvider, useDwSettingsProvider } from "./DwSettings"
import { AnnuityFieldsProvider, useAnnuityFields } from "./AnnuityFields"

// agents: {['$agentId]}
function getAgent(agentLinks: AgentLink[], agents: Record<string, Agent>, type: string) {

    const agentsLookup = agentLinks.reduce((acc, l) => {
        if (l.linkType === type) {
            const agent = agents[l.agentId]
            return agent ? {...acc, [l.familyMemberId]: [...(acc[l.familyMemberId] ?? []), agent]} : acc
        } else {
            return acc
        }
    }, {})

    return (member) => {
        return agentsLookup[member.familyMemberId]
    }
}


export interface SortButtonProps {
    searchField: string; 
    sortField: string; 
    sortOrder: number; 
    setSortField: (field: string | ((field: string) => string)) => void; 
    setSortOrder: (order: number | ((order: number) => number)) => void
}

function SortButtonInContext({searchField}: {searchField: string}) {
    const {sortField, sortOrder, setSortField, setSortOrder} = useDwSettingsProvider()
    return <SortButton {...{searchField, sortField, sortOrder, setSortField, setSortOrder}} />
}

export function SortButton({searchField, sortField, sortOrder, setSortField, setSortOrder}: SortButtonProps) {
    const isSorting = searchField === sortField
    const buttonElement =
        (searchField === sortField)
            ? sortOrder === 1 
                ? <IconChevronDown /> 
                : <IconChevronUp />
            : <IconArrowUpDown />
    return (
        <button className={isSorting ? "text-pcx-800" : "text-pcx-800/50"} onClick={() => {
            if (isSorting) {
                setSortOrder(s => s * -1)
            } else {
                setSortField(searchField)
                setSortOrder(+1)
            }
        }}>
            {buttonElement}
        </button>
    )
}

export default function DataWarehouse() {

    const groupableFields = useGroupableFields()
    // TODO: Move pre-calculated data to a provider on the *TOP* level
    return (
      <IncomeProvider>
        <AnnuityFieldsProvider>
          <DataWareHouseDataProvider>
            <DwSettingsProvider entity={family_member} defaultGroupBy={byFamilyMember} allowedValues={groupableFields}>
              <QueriesProvider entity={family_member}>
                <RowsProvider>
                  <DataWarehouseUI />
                </RowsProvider>
              </QueriesProvider>
            </DwSettingsProvider>
          </DataWareHouseDataProvider>
        </AnnuityFieldsProvider>
      </IncomeProvider>
    )
}

const maxFamilies = 150

type DwFunction = (
    f: Member | Family | Invention | Commodity | {products: Commodity[], members: Member[]},
) => any 

const DataWareHouseData = createContext({
        isLoadingAnnuity: false,
        scoresById: {} as Record<number, number>,
        agents: {} as Record<string, Agent>,
        agentLinks: [] as AgentLink[],
        tagsById: [] as Record<number, string[]>,
        claimScopesByMember: {} as Record<number, number[]>,
        membersPriorityDates: {} as Record<number, {date: string, internalReference: string, pctRouteFiling: boolean}>,
        //get_comments: (entity: string, entityId: number) => ([] as string[]) ,
        get_comments: (entity: string, entityId: number) => '' as string,
        get_last_comment: (entity: string, entityId: number) => ('' as string),
        dwFields: {} as Record<string, Record<string, DwFunction>>,
        searchableFields: {} as Record<string, (f: Member | Family | Invention | Commodity) => any>,
        displayFields: {} as Record<string, string>,
})

function DataWareHouseDataProvider({children}) {
    //console.log('recalculating DP')
    const {t} = useTranslation()
    const {hasClaimScopes, hasCosts, hasInnovation, hasAbandonmentDate, hasProductOwners, hasProductTags, hasRevenues} = useRoles()
    const {team} = useSingleTeamMangement()

    const currency = team?.currency ?? 'EUR'
    const {fxConverter} = useFxContext()
    const groupableFields = useGroupableFields()

    const { claims } = useClaims()
    const { tagsLookup } = useTags()
    const { agentLinks: _agentLinks, agents: _agents } = useAgents()
    const { membersByFamilyId: allMembersByFamilyId } = usePatents()
    const { families: _families, members: _members, familyById } = useFilteredPatents()
    const { commentsLookUp } = useComments()
    const { costsByMemberId } = useCosts()
    const { commodityAgentsByCommodityId } = useProducts()

    const {annuityFields, isLoading: isLoadingAnnuity} = useAnnuityFields()
    // console.log({annuityFields})

    // Revenues
    const {incomeLookup, years}: {years: number[], incomeLookup: IncomeLookup} = useIncome()
    const get_income = !hasRevenues ? undefined : (commodity: Commodity, member: Member, year: number) => {
        const income = incomeLookup[commodity.commodityId]?.[member.countryCode]?.[year]
        // console.log({ref: commodity.commodityReference, country, year, income})
        if (income && does_patent_cover_income(member, income.countryCode)) {
            return fxConverter.convert(income.amount, income.currency, currency)
        } else {
            return undefined
        }
    }
    // last 5 income years
    const incomeYears = _(years).takeRight(5).value()
    let incomeFunctions = undefined
    if (hasRevenues) {
        // console.log("Has revenues")
        //console.log({incomeYears, incomes})
        incomeFunctions = {}
        _(incomeYears).forEach(year => {
            incomeFunctions[`income-${year}`] = ({members, products}: {products: Commodity[], members: Member[]}) => 
                [_(products)
                    .flatMap(commodity => members.map(member => get_income(commodity, member, year)))
                    .filter(Boolean)
                    .sum()]
            incomeFunctions[`debug-income-${year}`] = ({members, products}: {products: Commodity[], members: Member[]}) => 
                _(products)
                    .flatMap(commodity => members.map(member => {
                        const inc = get_income(commodity, member, year)
                        return inc ? `${commodity.commodityReference}/${member.countryCode}/${currencyFormat.format(inc)}` : null
                    }))
                    .filter(Boolean)
                    //.join(', ')
                    .value()
            // console.log("Adding year " + year)
        })
    }

    const { scoresLookup } = useValuations()
    const scoresById = _.mapValues(scoresLookup, ss => {
        const score = calcScore(ss)
        return score
    })

    /// Limit the number of families to be processed
    // TODO: limiting the numbers of families in useRow
    const families = _.sortBy(_families.slice(0, maxFamilies), f => f.internalReference)
    const familyIds = new Set(families.map(f => f.patentFamilyId))
    const members = _members.filter(m => familyIds.has(m.patentFamilyId))

    const relevantMembers = new Set(members.map(m => m.familyMemberId))
    const agentLinks = _agentLinks.filter(l => relevantMembers.has(l.familyMemberId))

    const tagsById = tagsLookup[patent_family] ?? []

    const agents = _(_agents).map(a => [a.agentId, nameOf(a)]).fromPairs().value()
    
    const claimScopesByMember = claims
        .filter(c => relevantMembers.has(c.familyMemberId) && c.claimScopeId)
        .reduce((acc, cur) => {
            if (cur.claimScopeId)
                return { ...acc, [cur.familyMemberId]: _.sortBy([...(acc[cur.familyMemberId] ?? []), cur.claimScopeId]) }
            else
                return acc
        }, {})

    const membersPriorityDates = useMemo(
      () => {
        //console.log("Calculating the priority dates")
        return _.mapValues(allMembersByFamilyId, members => calculatePriorityDate(members))
      },
      [allMembersByFamilyId],
    )

    function get_comments(entity: string, entityId: number) {
        return commentsLookUp[entity]?.[entityId]?.map(c => c.comment?.trim())?.filter(c => c && c !== '')?.join('\n')
    }
    function get_last_comment(entity: string, entityId: number) {
        return commentsLookUp[entity]?.[entityId]?.[0]?.comment?.trim() ?? ''
    }

    const yes = t('yes')
    const no = t('no')

    function onlyDefinedBoolean(b?: boolean) {
        return b === undefined ? [] : [b ? yes : no]
    }

    const dwFields = {
        family: {
            dw_image: (f: Family) => f.patentFamilyId,
            comments: (f: Family) => get_comments(patent_family, f.patentFamilyId),
            lastComment: (f: Family) => get_last_comment(patent_family, f.patentFamilyId),
            patentFamilyReference: (f: Family) => f.internalReference,
            patentFamilyExtReference: (f: Family) => f.externalReference ?? '',
            familyName: (f: Family) => [f.familyName],
            patentFamilySummary: (f: Family) => f.summary ?? '',
            priorityDate: (f: Family) => familyById[f.patentFamilyId]?.priorityDate ?? membersPriorityDates[f.patentFamilyId]?.date ?? '',
            tags: (f: Family) => tagsById[f.patentFamilyId],
        },
        'family-member': {
            applicant: getAgent(agentLinks, agents, "applicant"),
            applicationDate: (m: Member) => [m.applicationDate],
            applicationNumber: (m: Member) => [m.applicationNumber],
            dw_claimScope: hasClaimScopes ? (m: Member) => claimScopesByMember[m.familyMemberId] ?? [] : undefined,
            countryCode: (m: Member) => [m.countryCode],
            familyMemberStatus: (m: Member) => [t([m.familyMemberStatus])],
            firstFiling: (m: Member) => onlyDefinedBoolean(m.firstFiling),
            familyMemberReference: (m: Member) => [m.internalReference],
            familyMemberExtReference: (m: Member) => [m.externalReference],
            inventor: getAgent(agentLinks, agents, "inventor"),
            ipType: (m: Member) => [t([m.ipType])],
            numberClaims: (m: Member) => [m.numberClaims],
            costs: !hasCosts ? undefined : ((m: Member) => accumulateCosts(costsByMemberId[m.familyMemberId] ?? [], {fxConverter, inclVat: false, currency}).total),
            owner: getAgent(agentLinks, agents, "owner"),
            optOut: (m: Member) => optOutMeaningful(m) ? onlyDefinedBoolean(m.optOut) : [],
            patentDate: (m: Member) => [m.patentDate],
            patentNumber: (m: Member) => [m.patentNumber],
            patentOfficeLink: (m: Member) => [m.patentOfficeLink],
            pctRouteFiling: (m: Member) => onlyDefinedBoolean(m.pctRouteFiling),
            expiryDate: (m: Member) => [m.expiryDate],
            abandonmentDate: hasAbandonmentDate ? ((m: Member) => [m.abandonmentDate]) : undefined,
            publicationDate: (m: Member) => [m.publicationDate],
            publicationNumber: (m: Member) => [m.publicationNumber],
            patentComments: (m: Member) => get_comments(family_member, m.familyMemberId),
            lastPatentComment: (m: Member) => get_last_comment(family_member, m.familyMemberId),
            dw_score: (m: Member) => [scoresById[m.familyMemberId]],
            title: (m: Member) => [m.title],
            unitaryPatent: (m: Member) => m.countryCode === 'EP' ? onlyDefinedBoolean(m.unitaryPatent) : [],
            validated: (m: Member) => onlyDefinedBoolean(m.validated),
        },
        'annuities': annuityFields,
        'products': {
            commodityReference: (c: Commodity) => c.commodityReference,
            commodityClass: (c: Commodity) => c.commodityClass,
            commodityDescription: (c: Commodity) => c.commodityDescription,
            isThirdParty: (c: Commodity) => c.isThirdParty ? yes : no,
            productImage: (c: Commodity) => c.commodityId,
            'productOwner': hasProductOwners && ((c: Commodity) => commodityAgentsByCommodityId[c.commodityId]?.map(l => agents[l.agentId])?.filter(Boolean)),
            productTags: hasProductTags && ((c: Commodity) => tagsLookup['commodity']?.[c.commodityId] ?? []),
        },
        'inventions': hasInnovation && {
            inventionReference: (i: Invention) => i.reference,
            inventionReferenceDate: (i: Invention) => i.referenceDate,
            inventionTitle: (i: Invention) => i.title,
            inventionSummary: (i: Invention) => i.summary,
        },
        'income': hasRevenues && incomeFunctions,
    }
    // console.log({dwFields})

    const searchableFields = _(dwFields).toPairs()
        .filter(([group, fields]) => typeof fields === 'object')
        .flatMap(([group, fields]) => _(fields).toPairs().filter(([field, fct]) => fct !== undefined).value())
        .fromPairs()
        .value()

    const displayFields = Object.fromEntries([
        ...groupableFields,
        ..._(searchableFields).toPairs().flatMap(([group, fields]) => [group, ..._.keys(fields)]).value()
    ].map(f => {
        if (typeof f === 'string' && f.includes('income-')) {
            const parts = f.split('-')
            if (parts.length == 3) {
                return [f, `DEBUG ${t('income')} ${parts[2]}`]
            } else {
                return [f, `${t('income')} ${parts[1]}`]
            }
        }
        return [f, t(f)]
    }))

    //console.log({displayFields})

    const value = {
        isLoadingAnnuity,
        scoresById,
        agents,
        agentLinks,
        tagsById,
        claimScopesByMember,
        membersPriorityDates,
        get_comments,
        get_last_comment,
        dwFields,
        searchableFields,
        displayFields,
    }

    return <DataWareHouseData.Provider {...{ value }}>{children}</DataWareHouseData.Provider>
}

function DataWarehouseSettings() {
    const {dwFields, displayFields} = useContext(DataWareHouseData)
    const {t} = useTranslation()
    const {groupBy, setGroupBy, searchFields, setSearchFields, doOuterJoin, setDoOuterJoin} = useDwSettingsProvider()
    const {hasJoin} = useRoles()
    const availableTags = _(dwFields)
        .toPairs()
        .map(([k, vs]) => [t(k), _(vs).keys().filter(v => vs[v] !== undefined && displayFields[v] !== undefined).sortBy(v => displayFields[v] ?? v).value()])
        .fromPairs()
        .value()

    //console.log({availableTags, displayFields})

    return <RenderedDatawarehouseSettings {...{groupBy, setGroupBy, availableTags, searchFields, setSearchFields, displayFields, doOuterJoin, setDoOuterJoin, hasJoin}} />
}

const RenderedDatawarehouseSettings = React.memo((
    {groupBy, setGroupBy, availableTags, searchFields, setSearchFields, displayFields, doOuterJoin, setDoOuterJoin, hasJoin}: 
    {groupBy: string, setGroupBy: (s: string) => void, availableTags: Record<string, string[]>,
        searchFields: string[], setSearchFields: (ss: string[]) => void, displayFields: Record<string, string>,
        doOuterJoin: boolean, setDoOuterJoin: (doOuterJoin: boolean) => void, hasJoin: boolean,
    }) => {

    const {t} = useTranslation()

    const groupableFields = useGroupableFields()

    const inputGroupStyle="flex flex-row items-center gap-2 max-w-3xl"
    const labelStyle="w-24 after:content-[':'] shrink-0"

    //console.log('rerender settings')

    return (
        <div className="flex flex-col gap-2">
            <div className={inputGroupStyle}>
                <label className={labelStyle}>{t('grouping')}</label>
                <select
                    className="form-select text-sm w-40 h-7 py-px px-2"
                    value={groupBy}
                    onChange={(e) => {
                        const gb = e.target.value
                        setGroupBy(gb)
                    }}>
                    {groupableFields.map(f => <option key={f} value={f}>{t([f])}</option>)}
                </select>

                {hasJoin && <label className="ml-8 flex flex-row gap-2 items-center">
                    <span className="">Outer Join:</span>
                    <ToggleButton {...{checked: doOuterJoin, setChecked: setDoOuterJoin}}>
                    </ToggleButton>
                </label>}
            </div>

            <div className={inputGroupStyle}>
                <label className={labelStyle}>{t('fields')}</label>
                <TagList {...{
                    name: "searchFields",
                    dragable: true,
                    // TODO: check if we have to sort out undefined functions
                    availableTags,
                    tags: searchFields,
                    setTags: setSearchFields,
                    tagDisplays: displayFields,
                    placeholder: t('field'),
                }} />
            </div>
        </div>
    )
}, _.isEqual)


function DataWarehouseUI() {
    //console.log('recalculating X')
    const { t } = useTranslation()

    const { setErrorMessage } = useMessages()
    const { team } = useAuth()
    const { hasAnnuities } = useRoles()

    const {
        isLoadingAnnuity,
        dwFields,
        searchableFields,
        displayFields,
    } = useContext(DataWareHouseData)

    const { families: allFamilies, } = usePatents()
    const { families: _families } = useFilteredPatents()
    const { imagesLookup } = useBackend()
    const { claimScopeById } = useClaims()

    /// Limit the number of families to be processed
    // TODO: limiting the numbers of families in useRow
    const families = _.sortBy(_families.slice(0, maxFamilies), f => f.internalReference)

    
    const noAggregagation = new Set([
        'comments',
        'patentComments',
        'lastPatentComment',
        'lastComment',
        'dw_claimScope',
        'applicationDate',
        'applicationNumber',
        'expiryDate',
        'publicationDate',
        'publicationNumber',
        'patentDate',
        'patentNumber',
        'patentFamilyReference', 
        'familyMemberReference',
        'patentFamilyExtReference',
        'patentFamilySummary',
        'patentOfficeLink',
        'familyMemberExtReference',
        'title',
        'familyName',
        'dw_image',
        'dw_score',
        'dm_validation_errors',
        'commodityReference',
        'productImage',
        'commodityDescription',
        'inventionReference',
        'inventionReferenceDate',
        'inventionTitle',
        'inventionSummary',
    ])

    const {rows} = useRowsProvider()
    const {searchFields, sortOrder, sortField} = useDwSettingsProvider()
    const {selectedQuery, showDeleteModal, showOpenModal, showSaveAsModal} = useQueriesProvider()

    const sortIndex = searchFields.indexOf(sortField)
    const collator = new Intl.Collator()

    const validValue = (v) => v !== undefined && ((typeof v === 'string' && v.trim() !== "") || typeof v === 'object' || typeof v === 'number')

    //console.log({dwFields})
    // TODO: add product filter to bar (own/third party and filter word)
    const rowData = rows
        .map(row => ({
            ...row,
            columns: searchFields.map(f => {
                let values = []
                if (f in dwFields['family']) {
                    values = row.families.map(fam => dwFields['family'][f](fam)).filter(validValue)
                } else if (f === 'dw_score') {
                    const scores = _(row.members).flatMap(m => searchableFields[f](m)).filter(validValue).value()
                    const score = _.mean(scores)
                    if (isNaN(score))
                        values = []
                    else 
                        values = [score.toFixed(1)]
                } else if (f === 'fees' && dwFields['annuities']) {
                    return _(row.members ?? [])
                        .flatMap(m => _.toPairs(dwFields['annuities'][f](m))) //, annuityContext))) // ERROR Not a function
                        .reduce((acc, [ccy, amount]) => ({...acc, [ccy]: parseFloat((acc[ccy] ?? 0)) + parseFloat(amount as string)}), {})
                } else if (row.claimScopeIds && f === 'dw_claimScope') {
                    return Object.fromEntries(row.claimScopeIds.map(c => [c, 1]))
                } else if (typeof dwFields['family-member'][f] === 'function')
                    values = row.members.flatMap(m => dwFields['family-member'][f](m)).filter(validValue)
                else if (typeof (dwFields['annuities'] || {})[f] === 'function')
                    values = row.members.flatMap(m => dwFields['annuities'][f](m/*, annuityContext*/)).filter(validValue)
                else if (typeof dwFields['products'][f] === 'function')
                    values = row.products.filter(Boolean).map(p => dwFields['products'][f](p)).flat().filter(validValue)
                else if (typeof (dwFields['inventions'] || {})[f] === 'function')
                    values = row.inventions.filter(Boolean).map(i => dwFields['inventions'][f](i)).filter(validValue)
                else if (typeof (dwFields['income'] || {})[f] === 'function') {
                    values = dwFields['income'][f]({products: row.products, members: row.members}) ?? []
                    if (f.startsWith('income'))
                        console.log({values})
                    //return values
                }
                return _.countBy(values)
            })
        }))
        .sort((rowA, rowB) => {
            if (sortIndex < 0) return 0
            const a = Object.keys(rowA.columns[sortIndex]).sort()?.[0]
            const b = Object.keys(rowB.columns[sortIndex]).sort()?.[0]
            return a === undefined ? 1 : b === undefined ? -1 : (collator.compare(a, b) * sortOrder)
        })
        .filter(({columns}) => columns.find(c => _.size(c) > 0))

    // Loops over all rows to get the total counts
    const totalCounts = searchFields.map((f, fi) =>
        noAggregagation.has(f)
            ? {Total: _.sum(rowData.map(r => _.size(r.columns[fi])))}
            : f === "numberClaims"
            ? {Total: _(rowData).map(r => dotProduct(r.columns[fi])).sum()} 
            : f === "costs"
            ? {Total: currencyFormat.format(_(rowData).map(r => dotProduct(r.columns[fi])).sum())}
            : f.startsWith('income-')
            ? {Total: currencyFormat.format(_(rowData).map(r => dotProduct(r.columns[fi])).sum())}
            : f.startsWith('debug-income-')
            ? {}
            : _(rowData).flatMap(r => _.toPairs(r.columns[fi])).filter(([key]) => validValue(key)).groupBy(([key]) => key).mapValues(v => _.sum(v.map(([k,v]) => v))).value()
    )
    const tableData = {rows: rowData, columnKeys: searchFields, columns: searchFields.map(f => t([f])), totalCounts}
                        
    const maxRows = 500
    //const doDownload = () => downloadReport({ url: "/api/excel", report: "data-report", opts: { tableData } }).catch((err) => setErrorMessage(err.message))
    const doDownload = () => dwExcelExport(tableData, imagesLookup, claimScopeById, team).catch((err) => setErrorMessage(err.message))
    const hasAnnuityField = searchFields.some(f => dwFields['annuities'] && f in (dwFields['annuities'] ?? {}))
    const exportButton = (!hasAnnuityField || !isLoadingAnnuity || !hasAnnuities)
        ? <LoadingButton onClick={doDownload} className="btn-secondary px-2 py-px font-normal text-sm w-fit">
            {t('excel-export')}
        </LoadingButton>
        : <Popover className="relative">
            <PopoverButton className="btn-secondary font-normal text-base py-px">
                {t('excel-export')}
            </PopoverButton>
            <PopoverPanel className="bg-white rounded-md shadow-md border border-pcx-200 z-40" anchor={{ to: 'bottom end', gap: 2 }}>
                <p className="p-4">
                    {t("renewals-still-loading")}
                </p>
                <div className="p-4 bg-pcx-200 flex flex-row-reverse gap-4">
                    <button className="btn-primary font-normal text-base py-px" onClick={doDownload} >
                        {t('export-anyway')}
                    </button>
                    <PopoverButton className="btn-secondary font-normal text-base py-px">
                        {t('cancel')}
                    </PopoverButton>
                </div>
            </PopoverPanel>
        </Popover>
    return (
        <>
            {/* @ts-ignore */}
            <Helmet>
                <title>{t('data-wizard')} | Patent Cockpit</title>
            </Helmet>
            <div className="header-row">
                <div className="flex flex-row gap-2">
                    <h2 className="modern-h2 grow">{t('data-wizard')}</h2>
                    {exportButton}
                </div>
            </div>
            <div className="main-content">
                <div className="flex flex-col lg:flex-row-reverse justify-between py-2 gap-4 w-visible">
                    <div className="flex lg:flex-col sm:flex-row flex-col lg:items-end sm:items-center gap-2">
                        <h3 className="text-slate-600 whitespace-nowrap">{selectedQuery?.queryName ?? t('unnamed-query')}</h3>
                        <QueryMenu />
                    </div>

                    {showSaveAsModal && <QueryEditorModal />}
                    {showDeleteModal && <QueryDeleteModal />}
                    {showOpenModal && <QueryOpenModal />}

                    <DataWarehouseSettings key={123} />
                </div>
                {tableData.rows.length >= maxRows &&
                    <div className="py-2">
                        <div className="warning">
                            <Trans i18nKey="only-maxRows-shown" values={{ maxRows }} />
                        </div>
                    </div>
                }
                {families.length === 0
                    ? <NoFamilies {...{ families, allFamilies }} />
                    : searchFields.length === 0
                        ? <div className="text-xl text-gray-700 py-10">{t('data-wizard-empty')}</div>
                        : <div className="py-4">
                            <table className="border-spacing-0 border-collapse">
                                <thead className="">
                                    <tr className="text-left">
                                        {searchFields.map(searchField =>
                                            <th key={searchField} className="sticky -top-4 bg-white dark:bg-pcx-100 px-0">
                                                <h3 className="whitespace-nowrap px-2 border-b-2 border-pcx-500">
                                                    {displayFields[searchField]} <SortButtonInContext {...{ searchField }} />
                                                </h3>
                                            </th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {tableData.rows.slice(0, maxRows).map(row =>
                                        <tr key={row.key} className="last:border-b-2 last:border-pcx-500 even:bg-pcx-100 dark:even:bg-pcx-200">
                                            {row.columns.map((values, colIdx) =>
                                                <td key={`${row.key}-${colIdx}`} className="align-top py-0.5 px-2">
                                                    <RenderCell {...{ values, field: searchFields[colIdx] }} />
                                                </td>
                                            )}
                                        </tr>
                                    )}
                                    {tableData.rows.length >= maxRows &&
                                        <tr className="warning">
                                            <td className="text-slate-500 px-2 py-1" colSpan={searchFields.length}>
                                                <Trans i18nKey="only-maxRows-shown" values={{ maxRows }} />
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                                <tfoot>
                                    <tr className="border-t-2 border-gray-800 align-top">
                                        {_.zip(searchFields, totalCounts).map(([field, counts]) =>
                                            <td key={field}>
                                                <div className="px-2 flex flex-col">{
                                                    Object.entries(counts)
                                                        .sort(([va, ca], [vb, cb]) => cb - ca)
                                                        .map(([value, count]) => <div key={`count-${value}`}>{value}: {count}</div>)
                                                }</div>
                                            </td>
                                        )}
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                }
            </div>
        </>
    )
}

function dotProduct(values: Record<number, number>) {
    return _(values).entries().map(([k, v]) => (+k) * v).sum()
}

const currencyFormat = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })


function optOutMeaningful(member: Member) {
    return (member.countryCode === 'EP' && member.unitaryPatent !== true) || member.validated
}

// values: key of value to count
function RenderCell({ values, field }) {
    const { imagesLookup } = useBackend()
    const { claimScopeById } = useClaims()
    const { commodityById } = useProducts()
    const { familyById } = useFilteredPatents()

    const possibleRef = Object.keys(values)[0]
    const linkStyle = "underline-link py-0 whitespace-nowrap after:content-[';'] mr-1 last:mr-0 last:after:content-['']"

    function onlyKeys() {
        return _(values).keys().sortBy().value()
    }

    if (field === "patentFamilyReference" && possibleRef) {
        return <>{
            _(values)
                .keys().sortBy()
                .map(internalReference =>
                    <Link key={internalReference} className={linkStyle} to={familyUrl({ internalReference })}>
                        {internalReference.trim()}
                    </Link>)
                .value()
        }</>
    } else if (field === "familyMemberReference") {
        return (
            <div className="flex flex-wrap"> {onlyKeys().map((ref) =>
                <Link key={ref} className={linkStyle + " pr-1"} to={memberUrl({ internalReference: ref })}>
                    {ref.trim()}
                </Link>
            )}</div>
        )
    } else if (field === "inventionReference") {
        return (
            <div className="flex flex-wrap"> {onlyKeys().map((ref) =>
                <Link key={ref} className={linkStyle + " pr-1"} to={inventionUrl({ reference: ref })}>
                    {ref.trim()}
                </Link>
            )}</div>
        )
    } else if (field === "dw_image" && possibleRef) {
        // possibleRef is the patent_family_id 
        const url = imagesLookup[patent_family]?.[possibleRef]?.url // NOTE: This only takes the first image instead of all if there is a group
        if (url)
            return (
                <div className="w-36 max-h-[4rem]">
                    <img className="mx-auto h-[4rem]" src={url} alt={`Family ${familyById[possibleRef]?.internalReference ?? ''}`} />
                </div>
            )
        else return null
    } else if (field === "commodityReference") {
        return (
            <div className="flex flex-wrap"> {onlyKeys().map((reference) =>
                <Link key={reference} className={linkStyle + " pr-1"} to={commodityReferenceUrl({ reference })}>
                    {reference.trim()}
                </Link>
            )}</div>
        )
    } else if (field === "productImage" && possibleRef) {
        // possibleRef is the patent_family_id 
        const url = imagesLookup['commodity']?.[possibleRef]?.url // NOTE: This only takes the first image instead of all if there is a group
        return url ? (
            <div className="w-36 max-h-[4rem]">
                <img className="mx-auto h-[4rem]" src={url} alt={`Product ${commodityById[possibleRef]?.internalReference ?? ''}`} />
            </div>
        ) : null
    } else if (field === "dw_claimScope") {
        const scopes = _.flatten(Object.keys(values))
        //console.log(values)
        //console.log(scopes)
        // ?.map(c => claimScopesById[c])
        return (
            <div className="flex flex-row gap-2">
                {scopes.map((c, ci) =>
                    <div key={ci} className="w-64 flex flex-col gap-1">
                        <div className="h-36">
                            <PlainImage {...{
                                entity: claim_scope,
                                entityId: c,
                                clickable: false,
                            }} />
                        </div>
                        <div
                            key={ci}
                            className="bg-pcx-100 dark:bg-pcx-200 p-1 rounded-md max-w-prose border border-pcx-200"
                            dangerouslySetInnerHTML={{ __html: claimScopeById[c]?.claimScopeSummary }} />
                    </div>
                )}
            </div>
        )
    } else if (field === "numberClaims") {
        const total = dotProduct(values)
        return <div>{total === 0 ? "" : total}</div>
    } else if (field === "costs") {
        const total = dotProduct(values)
        return <div className="w-full text-right tabular-nums">{currencyFormat.format(total)}</div>
    } else if (field === "comments" || field === 'patentComments') {
        return <div>{_.keys(values).map((v, vi) => <p className="last:mb-0 whitespace-pre-line" key={vi}>{v}</p>)}</div>
    } else if (field === 'patentOfficeLink') {
        return <div className="flex flex-row flex-wrap gap-x-1">{
            _(values)
                .keys()
                .filter(v => typeof v === 'string' && v.trim() !== '')
                .map((v, vi) =>
                    <a
                        href={v} target="_blank" rel="noreferrer" title={v} key={vi}
                        className="whitespace-nowrap max-w-xs text-ellipsis overflow-hidden text-pcx-600 underline"
                    >{v}</a>)

                .value()
        }</div>
    } else if (field === "fees") {
        return <div className=" text-right tabular-nums">{_(values).toPairs().map(([ccy, v], vi) =>
            <span className="pr-1 last:pr-0 after:content-[';'] last:after:content-['']" key={vi}>{currencyFormat.format(v)} {ccy}</span>
        ).value()}</div>
    } else if (field.startsWith("income-")) {
        // console.log({field, values})
        //if (values == 0) return undefined
        return <div className=" text-right tabular-nums">{_(values).toPairs().map(([value], vi) =>
            <span key={vi} className="pr-1 last:pr-0 after:content-[';'] last:after:content-['']">{currencyFormat.format(parseFloat(value))}</span>
        ).value()}</div>
    } else if (field.startsWith("debug-income-")) {
        //console.log({field, values})
        if (values == 0 || _.size(values) === 0) return undefined
        return <div className="tabular-nums">{_(values).toPairs().map(([value], vi) =>
            <span key={vi} className="pr-1 last:pr-0 after:content-[';'] last:after:content-['']">{value}</span>
        ).value()}</div>
    } else {
        return (
            <div className="flex flex-wrap"> {onlyKeys().map((v) =>
                <span key={v} className="last:pr-0 pr-1 after:content-[';'] last:after:content-['']">{v}</span>
            )}</div>
        )
    }
}

function NoFamilies({ families, allFamilies }) {
    const { t } = useTranslation()

    return <div className="text-center border-t-2 border-pcx-500 pt-4 mt-2 mr-4">
        {families.length === allFamilies.length
            ? <>
                {t('extract-data-here')}
                <br />
                <Link className="text-pcx-500 underline-link" to="/patents/portfolio">{t('add-patents')}</Link>
            </>
            :
            <Trans i18nKey="no-patent-results" />
        }
    </div>
}

type TableData = {rows: (Row & {columns: {}[]})[], columnKeys: string[], columns: string[], totalCounts: ({ [x: string]: number; } | { Total: string; })[]}
function dwExcelExport(
  tableData: TableData,
  imagesLookup: Record<string, Record<number, ImageEntry>> = {},
  claimScopeById: Record<number, ClaimScope>,
  realm: string,
) {
  //console.log({ tableData, imagesLookup })

  const claimScopesIndex = tableData.columnKeys.indexOf('dw_claimScope')
  const hasClaimScopes = claimScopesIndex >= 0

  let header = tableData.columns
  let scopesHeader = []
  if (hasClaimScopes) {
    const maxScopes = _(tableData.rows)
      .map(r => _.size(r.columns[claimScopesIndex]))
      .max()
    scopesHeader = _.range(maxScopes).flatMap(i => [`Claim Scope ${i + 1} Image`, `Claim Scope ${i + 1}`])
    header = [...header.filter((h, i) => i !== claimScopesIndex), ...scopesHeader]
  }
  //console.log({ header })

  const rows = tableData.rows.map(row => {
    const normalColumns = row.columns
      .map((c, colIndex) => {
        const field = tableData.columnKeys[colIndex]
        // always return at least something (as placeholder)
        if (field === 'dw_image') {
          return imagesLookup[patent_family]?.[_(c).keys().head()]?.imageId ?? ""
        } else if (field === 'dw_claimScope') {
          return null
        } else if (field === 'productImage') {
          return imagesLookup['commodity']?.[_(c).keys().head()]?.imageId ?? ""
        } else if (field === 'costs' || field.startsWith('income-')) {
          return currencyFormat.format(dotProduct(c))
        } else if (field === 'numberClaims') {
          return dotProduct(c)
        } else if (field === 'fees') {
          return _(c).toPairs().map(([ccy, v]) => `${currencyFormat.format(+v)} ${ccy}`).join('; ') ?? ""
        } else {
          return _(c).keys().sortBy().join('; ') ?? ""
        }
      })
      .filter(c => c !== null)
    if (hasClaimScopes) {
      const claimScopes = row.columns[claimScopesIndex]
      const scopes = _(claimScopes)
        .keys()
        .flatMap(c => [imagesLookup[claim_scope]?.[c]?.imageId ?? "", claimScopeById[c]?.claimScopeSummary])
        .value()
      return [...normalColumns, ...scopes]
    } else {
      return normalColumns
    }
  })

  const styles = _(tableData.columnKeys)
    .zip(tableData.columns)
    .map(([field, name]) => [name, _styles[field]])
    .filter(([, style]) => style !== undefined)
    .concat(_(scopesHeader).chunk(2).flatMap(([image, html]) => [[image, 'image'], [html, 'html']]).value())
    .fromPairs()
    .value()

  const footer: string[] = tableData.totalCounts
    .filter((t, i) => i !== claimScopesIndex)
    .map(total =>
      'Total' in total
        ? `Total: ${total['Total']}`
        : _(total)
            .toPairs()
            .map(([key, value]) => `${key}: ${value}`)
            .sortBy()
            .join(', '),
    )

  const excelSheets = {
    sheets: [
      {
        name: 'Data Wizard',
        header,
        rows,
        styles,
        footer,
      },
    ],
  }
  return createExcelFile(excelSheets, `Data-Wizard-${realm}-${new Date().toISOString().slice(0, 10)}.xlsx`)
}

// image: product, family, claim scope (actually handled manually)
// link: po link
// html: claim scope summaries; handle manually
const _styles = {
    dw_image: 'image',
    productImage: 'image',
    dw_claimScope: 'image',
    patentOfficeLink: 'link',
}